/**
 * NotificationCenter.jsx
 *
 * Two-tab panel:
 *   Tab 0 — Email Templates  (notification_rule_templates where type='template')
 *   Tab 1 — Scheduled Reminders  (notification_rules type='scheduled' + calendar_events)
 *
 * Usage:
 *   <NotificationCenter />
 */

import React, { useState, useEffect, useCallback, useContext } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Skeleton,
  Tooltip,
  FormControlLabel,
  Checkbox
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ScheduleIcon from '@mui/icons-material/Schedule'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useTranslation } from 'react-i18next'
import DeleteDialog from '../../DeleteDialog'
import TemplateEditorDialog from '../ClienteDetalle/Alerts/TemplateEditorDialog'
import ScheduledReminders from './ScheduledReminders'
import {
  deleteTemplate,
  getTemplatesByUser // must filter WHERE type = 'template' server-side
} from '../../../services/notificationRules'
import { authContext } from '../../../contexts/AuthContext'

// ─── Brand helpers ────────────────────────────────────────────────────────────
const brandRadius = () => (window.$brand === 'PRB' ? 0 : 2)

// ─── LocaleChip ───────────────────────────────────────────────────────────────
const LocaleChip = ({ locale, filled }) =>
  filled ? (
    <Chip
      label={locale.toUpperCase()}
      size='small'
      color='primary'
      sx={{ height: 18, fontSize: 10, fontWeight: 700, mr: 0.5 }}
    />
  ) : (
    <Chip
      label={locale.toUpperCase()}
      size='small'
      variant='outlined'
      sx={{ height: 18, fontSize: 10, fontWeight: 700, mr: 0.5, opacity: 0.35 }}
    />
  )

// ─── TemplateRow ──────────────────────────────────────────────────────────────
const TemplateRow = ({ template, onPreview, onEdit, onDelete }) => {
  const { t } = useTranslation()
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
        transition: 'background 0.1s',
        '&:hover': { bgcolor: 'action.hover' },
        cursor: 'pointer'
      }}
      onClick={() => onPreview(template)}
    >
      <Box
        sx={{
          color: 'text.secondary',
          display: 'flex',
          mr: 1.5,
          flexShrink: 0
        }}
      >
        <DescriptionOutlinedIcon fontSize='small' />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant='body2' fontWeight={500} noWrap>
          {template.description || t('Untitled template')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25 }}>
          <LocaleChip locale='en' filled={template.has_en} />
          <LocaleChip locale='es' filled={template.has_es} />
          {template.label && (
            <Typography
              variant='caption'
              color='text.disabled'
              sx={{ ml: 0.5 }}
            >
              {template.label}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
        <Tooltip title={t('Edit')}>
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation()
              onEdit(template)
            }}
          >
            <EditOutlinedIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('Delete')}>
          <IconButton
            size='small'
            sx={{ color: '#d32f2f' }}
            onClick={(e) => {
              e.stopPropagation()
              onDelete(template)
            }}
          >
            <DeleteOutlinedIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>

      <ChevronRightIcon
        sx={{ fontSize: 18, color: 'text.disabled', ml: 0.5, flexShrink: 0 }}
      />
    </Box>
  )
}

// ─── SkeletonRows ─────────────────────────────────────────────────────────────
const SkeletonRows = ({ count = 4 }) =>
  Array.from({ length: count }).map((_, i) => (
    <Box
      key={i}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        gap: 1.5
      }}
    >
      <Skeleton variant='circular' width={20} height={20} />
      <Box sx={{ flexGrow: 1 }}>
        <Skeleton variant='text' width='55%' height={18} />
        <Skeleton variant='text' width='30%' height={14} />
      </Box>
      <Skeleton
        variant='rectangular'
        width={56}
        height={28}
        sx={{ borderRadius: 1 }}
      />
    </Box>
  ))

// ─── SectionHeader ────────────────────────────────────────────────────────────
const SectionHeader = ({ label, buttonLabel, onAdd, extra }) => (
  <Box
    sx={{
      px: 2,
      pt: 2,
      pb: 0.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}
  >
    <Typography
      variant='caption'
      color='text.secondary'
      fontWeight={600}
      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {extra}
      <Button
        variant='outlined'
        size='small'
        startIcon={<AddIcon />}
        sx={{ borderRadius: brandRadius(), py: 0.25, fontSize: 11 }}
        onClick={onAdd}
      >
        {buttonLabel}
      </Button>
    </Box>
  </Box>
)

// ─── TemplatesTab ─────────────────────────────────────────────────────────────
function TemplatesTab({ showSnack }) {
  const { t } = useTranslation()
  const { auth } = useContext(authContext)

  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorTemplate, setEditorTemplate] = useState(null)
  const [editorIsSystem, setEditorIsSystem] = useState(0)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [showHidden, setShowHidden] = useState(false)

  const loadTemplates = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getTemplatesByUser(auth.user.id)
      setTemplates(data)
    } catch {
      showSnack('error', t('Failed to load templates'))
    } finally {
      setLoading(false)
    }
  }, [auth.user.id, t])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  const openCreate = (isSystem) => {
    setEditorTemplate(null)
    setEditorIsSystem(isSystem)
    setEditorOpen(true)
  }

  const openEdit = (template) => {
    setEditorTemplate(template)
    setEditorIsSystem(template.is_system)
    setEditorOpen(true)
  }

  const openPreview = (template) => {
    setEditorTemplate({ ...template, is_system: 1 })
    setEditorIsSystem(1)
    setEditorOpen(true)
  }

  const handleSaved = (saved, wasCreate) => {
    loadTemplates()
    showSnack(
      'success',
      wasCreate ? t('Template created') : t('Template updated')
    )
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    try {
      await deleteTemplate(deleteTarget.id)
      setTemplates((prev) => prev.filter((tp) => tp.id !== deleteTarget.id))
      showSnack('success', t('Template deleted'))
    } catch (e) {
      const msg =
        e?.status === 409
          ? t('Template is in use by one or more rules and cannot be deleted')
          : e?.status === 403
            ? t('You do not have permission to delete this template')
            : t('Failed to delete template')
      showSnack('error', msg)
    } finally {
      setDeleteTarget(null)
    }
  }

  const systemTemplates = templates.filter(
    (tp) => tp.is_system === 1 || tp.is_system === true
  )
  const userTemplates = templates
    .filter((tp) => tp.is_system !== 1 && tp.is_system !== true)
    .filter(
      (tp) => showHidden || !(tp.is_hidden === 1 || tp.is_hidden === true)
    )

  return (
    <>
      <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
        {loading ? (
          <SkeletonRows />
        ) : (
          <>
            <SectionHeader
              label={t('System templates')}
              buttonLabel={t('New system template')}
              onAdd={() => openCreate(1)}
            />
            {systemTemplates.length > 0 ? (
              systemTemplates.map((tp) => (
                <TemplateRow
                  key={tp.id}
                  template={tp}
                  onPreview={openPreview}
                  onEdit={openEdit}
                  onDelete={setDeleteTarget}
                />
              ))
            ) : (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant='body2' color='text.disabled'>
                  {t('No system templates yet.')}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 0.5 }} />

            <SectionHeader
              label={t('User templates')}
              buttonLabel={t('New user template')}
              onAdd={() => openCreate(0)}
              extra={
                <FormControlLabel
                  control={
                    <Checkbox
                      size='small'
                      checked={showHidden}
                      onChange={(e) => setShowHidden(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant='caption' color='text.secondary'>
                      {t('Show hidden templates')}
                    </Typography>
                  }
                  sx={{ mr: 0 }}
                />
              }
            />
            {userTemplates.length > 0 ? (
              userTemplates.map((tp) => (
                <TemplateRow
                  key={tp.id}
                  template={tp}
                  onPreview={openPreview}
                  onEdit={openEdit}
                  onDelete={setDeleteTarget}
                />
              ))
            ) : (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant='body2' color='text.disabled'>
                  {t('No user templates yet.')}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      <TemplateEditorDialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={(saved) => handleSaved(saved, !editorTemplate)}
        template={editorTemplate}
        isSystem={editorIsSystem}
      />

      <DeleteDialog
        item={deleteTarget ?? {}}
        title={t('delete_template_title')}
        caption={t('delete_template_caption')}
        targetFunc={handleDeleteConfirm}
        handleDialogClose={() => setDeleteTarget(null)}
      />
    </>
  )
}

// ─── NotificationCenter ───────────────────────────────────────────────────────
export const NotificationCenter = () => {
  const { t } = useTranslation()
  const [tab, setTab] = useState(0)
  const [snack, setSnack] = useState({
    open: false,
    severity: 'success',
    message: ''
  })

  const showSnack = (severity, message) =>
    setSnack({ open: true, severity, message })

  return (
    <>
      <Paper
        elevation={0}
        variant='outlined'
        sx={{
          borderRadius: window.$brand === 'BLP' ? 3 : 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 400
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <Typography variant='subtitle2' fontWeight={700}>
            {t('Notification Center')}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {t(
              'Manage email templates and scheduled reminders for this portal'
            )}
          </Typography>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, py: 0, fontSize: 13 }
          }}
        >
          <Tab
            icon={<DescriptionOutlinedIcon sx={{ fontSize: 15 }} />}
            iconPosition='start'
            label={t('Templates')}
          />
          <Tab
            icon={<ScheduleIcon sx={{ fontSize: 15 }} />}
            iconPosition='start'
            label={t('Reminders')}
          />
        </Tabs>

        {/* Tab panels — both mounted so state is preserved on tab switch */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              display: tab === 0 ? 'flex' : 'none',
              flexDirection: 'column',
              flexGrow: 1,
              overflowY: 'auto'
            }}
          >
            <TemplatesTab showSnack={showSnack} />
          </Box>
          <Box
            sx={{
              display: tab === 1 ? 'flex' : 'none',
              flexDirection: 'column',
              flexGrow: 1,
              overflowY: 'auto'
            }}
          >
            <ScheduledReminders showSnack={showSnack} />
          </Box>
        </Box>
      </Paper>

      {/* Shared snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          elevation={6}
          variant='filled'
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default NotificationCenter
